import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Tomi.css";
import TomiPurchase from "./TomiPurchase";

export function isEmbeddedBrowser(userAgent) {
  return /MicroMessenger|QQ\/|QQ\b(?!Browser)|Weibo|DingTalk|AlipayClient|BytedanceWebview|aweme/i.test(
    userAgent,
  );
}

export function httpsUrl(value) {
  try {
    return new URL(value).protocol === "https:";
  } catch (_) {
    return false;
  }
}

export function androidReady(release) {
  return Boolean(
    release &&
      release.available &&
      httpsUrl(release.url) &&
      release.version &&
      release.size &&
      /^[a-f0-9]{64}$/i.test(release.sha256),
  );
}

export default function Tomi() {
  const [release, setRelease] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [guide, setGuide] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);
  const closeButton = useRef(null);
  const previousFocus = useRef(null);
  const embedded = isEmbeddedBrowser(navigator.userAgent);
  const apk = release && release.android;
  const ready = androidReady(apk);
  const ios = release && release.ios;
  const iosReady = Boolean(
    ios &&
      ios.available &&
      /^https:\/\/apps\.apple\.com\//.test(ios.appStoreUrl),
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "拓迷 Tomi · 复古掌机，随身相伴";
    const controller = new AbortController();
    fetch("/tomi/release.json", {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("release");
        return response.json();
      })
      .then(setRelease)
      .catch((error) => {
        if (error.name !== "AbortError") setLoadError(true);
      });
    return () => {
      controller.abort();
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (!guide) return;
    previousFocus.current = document.activeElement;
    closeButton.current.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") setGuide(false);
      if (event.key === "Tab") {
        event.preventDefault();
        closeButton.current.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", onKey);
      if (previousFocus.current) previousFocus.current.focus();
    };
  }, [guide]);

  return (
    <main className="tomi-page">
      <section className="tomi-download" id="download" aria-label="下载拓迷">
        <img className="tomi-icon" src="/tomi/icon.png" alt="拓迷应用图标" />
        <h1>
          拓迷 <span>Tomi</span>
        </h1>
        <p className="tomi-description">你的口袋复古掌机</p>
        <p className="tomi-features">本地导入 · 个性机身 · 独立存档</p>

        <div
          className={`tomi-buttons ${/iPhone|iPad|iPod/i.test(navigator.userAgent) ? "tomi-ios-first" : ""}`}
        >
          <div className="tomi-android">
            {ready ? (
              embedded ? (
                <button
                  className="tomi-button tomi-primary"
                  onClick={() => setGuide(true)}
                >
                  下载 Android 版
                </button>
              ) : (
                <a
                  className="tomi-button tomi-primary"
                  href={apk.url}
                  onClick={() => setDownloadStarted(true)}
                >
                  下载 Android 版
                </a>
              )
            ) : (
              <button className="tomi-button tomi-primary" disabled>
                {loadError
                  ? "下载暂不可用"
                  : release
                    ? "Android 安装包准备中"
                    : "正在读取下载信息…"}
              </button>
            )}
          </div>
          <div className="tomi-ios">
            {iosReady ? (
              <a className="tomi-button tomi-secondary" href={ios.appStoreUrl}>
                App Store 下载
              </a>
            ) : (
              <button className="tomi-button tomi-secondary" disabled>
                iOS 即将上线
              </button>
            )}
          </div>
        </div>

        <p className="tomi-version">
          {apk && apk.version && apk.size
            ? `Android ${apk.version} · ${apk.size}`
            : "Android 8.0 及以上 · ARM64"}
        </p>
        {loadError && (
          <p className="tomi-error" role="alert">
            下载信息加载失败，请刷新重试。
          </p>
        )}

        {downloadStarted && (
          <aside
            className="tomi-install-tip"
            role="status"
            aria-label="安装提示"
          >
            <strong>下载请求已发出</strong>
            <p>下载完成后，打开 APK 安装。</p>
            <p>如提示安装授权，仅为当前浏览器开启“允许安装此来源的应用”。</p>
            <p className="tomi-tip-note">
              暂未上架国内应用市场，尚无 APP
              备案号，系统可能提示或限制安装；请核对来源并按系统提示操作。
            </p>
          </aside>
        )}
      </section>

      <TomiPurchase />

      <footer className="tomi-footer">
        <p>
          <a href="mailto:tsc92321@gmail.com">联系支持</a>
          <span>·</span>
          <Link to="/">公司首页</Link>
        </p>
        <p>成都与子同袍科技有限公司</p>
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">
          蜀ICP备2021023183号
        </a>
      </footer>

      {guide && (
        <div
          className="tomi-guide"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tomi-guide-title"
        >
          <span className="tomi-guide-arrow" aria-hidden="true">
            ↗
          </span>
          <div className="tomi-guide-content">
            <h2 id="tomi-guide-title">点击右上角“···”</h2>
            <p>选择“在浏览器打开”，再下载拓迷。</p>
            <p className="tomi-guide-copy">
              没有该选项？复制链接到系统浏览器：
            </p>
            <p className="tomi-guide-url">{window.location.origin}/tomi</p>
            <button ref={closeButton} onClick={() => setGuide(false)}>
              知道了
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
